/* eslint-disable max-len */
/* eslint-disable indent */
import { useTranslation } from 'react-i18next';

import { DatePicker, MaskInput, Select, TextareaInput, TextInput } from 'components/FormControl';
import { PROJECT_ROLES } from 'constant';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import {
  getAcademicDegrees,
  getAcademicRanks,
  getGenders,
  handleDateOfSecretary,
} from 'utils/func';
import ResearcherDialog from '../ResearchersTable/ResearcherDialog';

export default function SecretarySection({
  control,
  errors,
  watch,
  disabled,
  setValue,
  organizations,
  projectRoles,
}) {
  const { t } = useTranslation();
  const researcherRef = useRef();
  const owner = watch('general.owner');
  const [contact, setContact] = useState(null);

  // #region Mutation

  const {
    fields: scientificSecretaryFields,
    append: appendScientificSecretary,
    update: updateScientificSecretary,
  } = useFieldArray({
    control,
    name: 'general.scientificSecretaries',
  });
  // #endregion

  // #region static data
  const genders = useMemo(() => getGenders(t), [t]);
  const getProjectRoleId = (roleCode) => {
    const projectRole = projectRoles.find((role) => role.code === roleCode);
    if (projectRole) return projectRole.id;
    return '';
  };

  const handleOpenAppendMemberModal = useCallback(
    (projectRoleId, append, order) => {
      researcherRef.current.open({
        projectRoleId,
        append,
        type: 'APPEND',
        value: { order },
      });
    },
    [researcherRef.current]
  );

  const handleOpenUpdateMemberModal = useCallback(
    (projectRoleId) => {
      researcherRef.current.open({
        projectRoleId,
        update: updateScientificSecretary,
        index: 0,
        value: scientificSecretaryFields?.[0],
        type: 'UPDATE',
      });
    },
    [researcherRef.current]
  );
  const projectRoleId = useMemo(() => getProjectRoleId(PROJECT_ROLES.SECRETARY), [projectRoles]);
  // #endregion

  useEffect(() => {
    const contactData = scientificSecretaryFields?.[0];
    // if saved -> data in projectProposalContact
    // if not saved -> data in contact.scientificProfile
    if (contactData) {
      const newScientificSecretary = {
        ...contactData,
        ...contactData.contact.scientificProfile,
        dob: handleDateOfSecretary(contactData, 'dob'),
        dateOfIssue: handleDateOfSecretary(contactData, 'dateOfIssue'),
        contactId: contactData.id,
        projectRoleId: projectRoles.find((role) => role.code === PROJECT_ROLES.SECRETARY)?.id,
      };
      setValue('general.scientificSecretary', newScientificSecretary);
      setContact({ ...contactData, ...contactData.contact.scientificProfile });
      // setIsDisabled(true);
    }
  }, [scientificSecretaryFields]);

  return (
    <>
      <div className="scientific-secretary">
        <div className=" mb-3 mt-4 flex gap-2 align-items-center">
          <h4 className="mr-2 mb-1">
            {t('projectProposal.general.scientificSecretarySection.label')}
          </h4>
          {scientificSecretaryFields.length === 0 && (
            <i
              onClick={() => {
                handleOpenAppendMemberModal(
                  projectRoleId,
                  appendScientificSecretary,
                  scientificSecretaryFields.length
                );
              }}
              className="pi pi-plus text-primary cursor-pointer bg-primary p-1 border-circle ml-1"
              data-pr-tooltip={t('formLayout.action.add')}
              data-pr-position="left"
            />
          )}
          {scientificSecretaryFields.length > 0 && (
            <i
              onClick={() => {
                handleOpenUpdateMemberModal(
                  projectRoleId,
                  updateScientificSecretary,
                  scientificSecretaryFields.length
                );
              }}
              className="pi pi-pencil text-primary cursor-pointer bg-primary p-1 border-circle ml-1"
              data-pr-tooltip={t('formLayout.action.add')}
              data-pr-position="left"
            />
          )}
        </div>

        <div className="grid">
          <div className="col-12 lg:col-4">
            <TextareaInput
              label={t(
                'projectProposal.general.scientificSecretarySection.scientificSecretary.name'
              )}
              name="general.scientificSecretary.fullname"
              isRequired
              control={control}
              errors={errors}
              disabled={disabled || !!contact?.fullname || scientificSecretaryFields.length === 0}
            />
          </div>
          <div className="col-12 lg:col-4">
            <Select
              label={t(
                'projectProposal.general.scientificSecretarySection.scientificSecretary.academicRank'
              )}
              name="general.scientificSecretary.academicRank"
              control={control}
              errors={errors}
              options={getAcademicRanks(t)}
              inputField="value"
              disabled={
                disabled || !!contact?.academicRank || scientificSecretaryFields.length === 0
              }
            />
          </div>
          <div className="col-12 lg:col-4">
            <Select
              label={t(
                'projectProposal.general.scientificSecretarySection.scientificSecretary.academicDegree'
              )}
              name="general.scientificSecretary.academicDegree"
              control={control}
              errors={errors}
              options={getAcademicDegrees(t)}
              inputField="value"
              disabled={
                disabled || !!contact?.academicDegree || scientificSecretaryFields.length === 0
              }
            />
          </div>
        </div>
        <div className="grid">
          <div className="col-12 lg:col-4">
            <DatePicker
              label={t(
                'projectProposal.general.scientificSecretarySection.scientificSecretary.dob'
              )}
              name="general.scientificSecretary.dob"
              control={control}
              errors={errors}
              disabled={disabled || !!contact?.dob || scientificSecretaryFields.length === 0}
            />
          </div>
          <div className="col-12 lg:col-4">
            <Select
              label={t(
                'projectProposal.general.scientificSecretarySection.scientificSecretary.gender'
              )}
              name="general.scientificSecretary.gender"
              control={control}
              errors={errors}
              options={genders}
              inputField="value"
              disabled={disabled || !!contact?.gender || scientificSecretaryFields.length === 0}
            />
          </div>
        </div>
        <div className="grid">
          <div className="col-12 lg:col-4">
            <MaskInput
              label={t(
                'projectProposal.general.scientificSecretarySection.scientificSecretary.citizenId'
              )}
              name="general.scientificSecretary.citizenId"
              control={control}
              errors={errors}
              mask={''.padStart(20, '9')}
              disabled={disabled || !!contact?.citizenId || scientificSecretaryFields.length === 0}
            />
          </div>
          <div className="col-12 lg:col-4">
            <DatePicker
              label={t(
                'projectProposal.general.scientificSecretarySection.scientificSecretary.dateOfIssue'
              )}
              name="general.scientificSecretary.dateOfIssue"
              control={control}
              errors={errors}
              disabled={
                disabled || !!contact?.dateOfIssue || scientificSecretaryFields.length === 0
              }
            />
          </div>
          <div className="col-12 lg:col-4">
            <TextareaInput
              label={t(
                'projectProposal.general.scientificSecretarySection.scientificSecretary.placeOfIssue'
              )}
              name="general.scientificSecretary.placeOfIssue"
              control={control}
              errors={errors}
              disabled={
                disabled || !!contact?.placeOfIssue || scientificSecretaryFields.length === 0
              }
            />
          </div>
        </div>
        <div className="grid">
          <div className="col-12 lg:col-4">
            <MaskInput
              label={t(
                'projectProposal.general.scientificSecretarySection.scientificSecretary.taxIdentificationNumber'
              )}
              name="general.scientificSecretary.taxIdentificationNumber"
              control={control}
              errors={errors}
              mask={''.padStart(20, '9')}
              disabled={
                disabled ||
                !!contact?.taxIdentificationNumber ||
                scientificSecretaryFields.length === 0
              }
            />
          </div>
          <div className="col-12 lg:col-4">
            <MaskInput
              label={t(
                'projectProposal.general.scientificSecretarySection.scientificSecretary.bankAccount'
              )}
              name="general.scientificSecretary.bankAccount"
              control={control}
              errors={errors}
              mask={''.padStart(30, '9')}
              disabled={
                disabled || !!contact?.bankAccount || scientificSecretaryFields.length === 0
              }
            />
          </div>
          <div className="col-12 lg:col-4">
            <TextareaInput
              label={t(
                'projectProposal.general.scientificSecretarySection.scientificSecretary.bank'
              )}
              name="general.scientificSecretary.bank"
              control={control}
              errors={errors}
              disabled={disabled || !!contact?.bank || scientificSecretaryFields.length === 0}
            />
          </div>
        </div>

        <div className="grid">
          <div className="col-12">
            <Select
              label={t('projectProposal.general.organization.name')}
              name="general.scientificSecretary.organizationId"
              options={organizations}
              disabled={
                disabled || !!contact?.organizationId || scientificSecretaryFields.length === 0
              }
              control={control}
              errors={errors}
              isRequired
            />
          </div>
          {watch('general.scientificSecretary.organizationId') &&
            organizations.find(
              (org) => org.id === watch('general.scientificSecretary.organizationId')
            )?.englishName === 'Other' && (
              <div className="col-12">
                <TextareaInput
                  label={t('projectProposal.general.organization.specificName')}
                  name="general.scientificSecretary.specificOrganizationName"
                  control={control}
                  errors={errors}
                  disabled={
                    disabled ||
                    !!contact?.specificOrganizationName ||
                    scientificSecretaryFields.length === 0
                  }
                />
              </div>
            )}
        </div>

        <div className="grid">
          <div className="col-12 lg:col-6">
            <MaskInput
              label={t(
                'projectProposal.general.scientificSecretarySection.scientificSecretary.mobile'
              )}
              name="general.scientificSecretary.mobile"
              control={control}
              errors={errors}
              mask={''.padStart(10, '9')}
              disabled={disabled || !!contact?.mobile || scientificSecretaryFields.length === 0}
            />
          </div>
          <div className="col-12 lg:col-6">
            <TextInput
              label={t(
                'projectProposal.general.scientificSecretarySection.scientificSecretary.email'
              )}
              name="general.scientificSecretary.email"
              control={control}
              errors={errors}
              disabled={disabled || !!contact?.email || scientificSecretaryFields.length === 0}
              isRequired
            />
          </div>
        </div>
        {/* end scientific secretary */}
      </div>

      <ResearcherDialog
        ref={researcherRef}
        organizations={organizations}
        ownerId={owner?.contact?.userId ?? ''}
      />
    </>
  );
}

SecretarySection.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  organizations: PropTypes.array,
  disabled: PropTypes.bool,
  control: PropTypes.shape({}).isRequired,
  errors: PropTypes.shape({}).isRequired,
  watch: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  projectRoles: PropTypes.array,
};

SecretarySection.defaultProps = {
  organizations: [],
  disabled: false,
  projectRoles: [],
};
